import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import CommandCenter from "@/pages/CommandCenter";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import PolicyDetail from "@/pages/PolicyDetail";
import ResourceList from "@/pages/ResourceList";
import Reports from "@/pages/Reports";
import Imports from "@/pages/Imports";
import Administration from "@/pages/Administration";
import Prospects from "@/pages/Prospects";
import AgencyTools from "@/pages/AgencyTools";
import Audit from "@/pages/Audit";
import ConflictCenter from "@/pages/ConflictCenter";
import ErrorCenter from "@/pages/ErrorCenter";
import FormsLibrary from "@/pages/FormsLibrary";
import EmailCenter from "@/pages/EmailCenter";
import HelpCenter from "@/pages/HelpCenter";
import MarketingModule from "@/pages/MarketingModule";
import AcceptInvite from "@/pages/AcceptInvite";
import Commissions from "@/pages/Commissions";
import Management from "@/pages/Management";
import BusinessRelationships from "@/pages/BusinessRelationships";
import RelationshipDetail from "@/pages/RelationshipDetail";
import DailyCallQueue from "@/pages/DailyCallQueue";
import CallWorkspace from "@/pages/CallWorkspace";
import BOIDashboard from "@/pages/BOIDashboard";
import BOIReports from "@/pages/BOIReports";
import AgencyCalendar from "@/pages/AgencyCalendar";
import ExpiringPolicies from "@/pages/ExpiringPolicies";
import BookOfBusiness from "@/pages/BookOfBusiness";
import Contacts from "@/pages/Contacts";
import Individuals from "@/pages/Individuals";
import Dashboard from "@/pages/Dashboard";
import PremiumChange from "@/pages/PremiumChange";
import Policies from "@/pages/Policies";
import Claims from "@/pages/Claims";
import Activities from "@/pages/Activities";
import ReportingCenter from "@/pages/ReportingCenter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { ConvertLead, ConvertOpp, CertActions, TaskComplete } from "@/pages/actions";
import { Anchor } from "lucide-react";

const LEAD_TABS = [{ to: "/leads", label: "Leads" }, { to: "/opportunities", label: "Opportunities" }];

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-deepnavy text-white">
      <div className="text-center animate-fade-in">
        <Anchor className="h-10 w-10 mx-auto text-sky mb-3" />
        <div className="font-serif text-2xl">ENDURANCE</div>
        <div className="text-xs text-sky/60 mt-1">Loading operating console…</div>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return children;
}

function ForcePasswordChange() {
  const { refresh } = useAuth();
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const submit = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { new_password: pw });
      toast.success("Password updated.");
      if (refresh) await refresh();
      window.location.href = "/";
    } catch (e) { toast.error("Could not update password."); setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-deepnavy px-4" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white rounded-sm p-8 border border-border">
        <div className="flex items-center gap-2 mb-4"><Anchor className="h-6 w-6 text-navy" /><span className="font-serif text-xl text-navy">Set a new password</span></div>
        <p className="text-sm text-mutedsea mb-4">Your administrator issued a temporary password. Please choose a new one to continue.</p>
        <Input type="password" data-testid="force-pw" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8)" className="mb-3" />
        <Input type="password" data-testid="force-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" className="mb-3" />
        <Button data-testid="force-pw-submit" disabled={busy} onClick={submit} className="w-full bg-navy hover:bg-atlantic text-white">{busy ? "Saving…" : "Update password"}</Button>
      </div>
    </div>
  );
}

function LoginRoute() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function RenewalsHeader(reload) {
  const gen = async () => {
    try { const { data } = await api.post("/renewals/generate"); toast.success(`Generated ${data.created} renewal(s).`); reload(); }
    catch { toast.error("Could not generate renewals."); }
  };
  return <Button key="gen" variant="outline" onClick={gen} data-testid="generate-renewals" className="border-atlantic text-atlantic"><RefreshCw className="h-4 w-4 mr-1.5" /> Generate Renewals</Button>;
}

function AppRoutes() {
  const { can, level } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/" element={<CommandCenter />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/relationships" element={<BusinessRelationships />} />
        <Route path="/relationships/:id" element={<RelationshipDetail />} />
        <Route path="/call-queue" element={<DailyCallQueue />} />
        <Route path="/call-workspace/:id" element={<CallWorkspace />} />
        <Route path="/boi-dashboard" element={<BOIDashboard />} />
        <Route path="/boi-reports" element={<BOIReports />} />
        <Route path="/calendar" element={<AgencyCalendar />} />
        <Route path="/expiring-policies" element={<ExpiringPolicies />} />
        <Route path="/book" element={<BookOfBusiness />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/individuals" element={<Individuals />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/agency-dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/producer-dashboard" element={<Navigate to="/dashboard" replace />} />
        <Route path="/premium-change" element={<PremiumChange />} />
        <Route path="/reporting" element={<ReportingCenter />} />
        <Route path="/leads" element={<ResourceList resourceKey="leads" tabs={LEAD_TABS} subtitle="Quick intake and pipeline for new prospects." actions={can.write ? (row, reload) => <ConvertLead row={row} reload={reload} /> : undefined} />} />
        <Route path="/opportunities" element={<ResourceList resourceKey="opportunities" tabs={LEAD_TABS} subtitle="Sales opportunities with carrier submissions and quotes." actions={can.write ? (row, reload) => <ConvertOpp row={row} reload={reload} /> : undefined} />} />
        <Route path="/policies" element={<Policies />} />
        <Route path="/policies/:id" element={<PolicyDetail />} />
        <Route path="/renewals" element={<ResourceList resourceKey="renewals" subtitle="No active expiration passes without a disposition." headerActions={can.write ? RenewalsHeader : undefined} />} />
        <Route path="/claims" element={<Claims />} />
        <Route path="/certificates" element={<ResourceList resourceKey="certificates" subtitle="Controlled certificate & evidence workflow with approval gating." actions={can.write ? (row, reload) => <CertActions row={row} reload={reload} /> : undefined} />} />
        <Route path="/commissions" element={<Commissions />} />
        <Route path="/management" element={level >= 80 ? <Management /> : <Navigate to="/" replace />} />
        <Route path="/administration" element={level >= 80 ? <Administration /> : <Navigate to="/" replace />} />
        <Route path="/prospects" element={<Prospects />} />
        <Route path="/agency-tools" element={level >= 80 ? <AgencyTools /> : <Navigate to="/" replace />} />
        <Route path="/service-cases" element={<ResourceList resourceKey="service_cases" subtitle="Every service request tracked as a case with owner, priority and SLA." />} />
        <Route path="/activities" element={<Activities />} />
        <Route path="/tasks" element={<ResourceList resourceKey="tasks" subtitle="Every open task has an owner." actions={can.write ? (row, reload) => <TaskComplete row={row} reload={reload} /> : undefined} />} />
        <Route path="/carriers" element={<ResourceList resourceKey="carriers" subtitle="Carrier, MGA and market directory." />} />
        <Route path="/documents" element={<ResourceList resourceKey="documents" subtitle="Document metadata — Dropbox is the authoritative repository." />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/imports" element={<Imports />} />
        <Route path="/audit" element={<Audit />} />
        <Route path="/forms" element={<FormsLibrary />} />
        <Route path="/conflicts" element={<ConflictCenter />} />
        <Route path="/errors" element={<ErrorCenter />} />
        <Route path="/email" element={<EmailCenter />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/marketing" element={<MarketingModule />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
